/* eslint-disable sonarjs/no-duplicate-string */
import { FormFailedMessage } from 'components/DynamicApiForm/DynamicApiForm';
import { DynamicApiLeadsForm } from 'components/DynamicApiForm/DynamicApiLeadsForm';
import Form from 'dataLayer/Form';
import { Lead } from 'dataLayer/Lead';
import { QUESTION_INPUT_VARIATIONS } from 'dataLayer/Question';
import AnimateGreenCheck from 'elements/Animations/AnimateGreenCheck';
import Loading from 'elements/Loading';
import Text from 'elements/Text';
import { useAsyncEffect } from 'hooks/useAsyncEffect';
import { useRouter } from 'next/router';
import React, { FC, useState } from 'react';
import * as yup from 'yup';

export const LEADS_CRS_FORM_SCHEMA_OVERRIDE = {
  EE_14: yup.string().required('Please fill in the above question'),
  LNG_5: yup.string().required('Please fill in the above question'),
  LNG_12: yup.string().required('Please fill in the above question'),
  LNG_6: yup.string().required('Please fill in the above question'),
  LNG_13: yup.string().required('Please fill in the above question'),

  // Language Scores
  EE_4: yup.string().required('Please fill in the above question'),
  EE_5: yup.string().required('Please fill in the above question'),
  EE_6: yup.string().required('Please fill in the above question'),
  EE_7: yup.string().required('Please fill in the above question'),
  CELP_1: yup.string().required('Please fill in the above question'),
  CELP_2: yup.string().required('Please fill in the above question'),
  CELP_3: yup.string().required('Please fill in the above question'),
  CELP_4: yup.string().required('Please fill in the above question'),
  PEAR_1: yup.string().required('Please fill in the above question'),
  PEAR_2: yup.string().required('Please fill in the above question'),
  PEAR_3: yup.string().required('Please fill in the above question'),
  PEAR_4: yup.string().required('Please fill in the above question'),
  TEF_1: yup.string().required('Please fill in the above question'),
  TEF_2: yup.string().required('Please fill in the above question'),
  TEF_3: yup.string().required('Please fill in the above question'),
  TEF_4: yup.string().required('Please fill in the above question'),
  TCF_1: yup.string().required('Please fill in the above question'),
  TCF_2: yup.string().required('Please fill in the above question'),
  TCF_3: yup.string().required('Please fill in the above question'),
  TCF_4: yup.string().required('Please fill in the above question'),
  EE_SPOUSE_4: yup.string().required('Please fill in the above question'),
  EE_SPOUSE_5: yup.string().required('Please fill in the above question'),
  EE_SPOUSE_6: yup.string().required('Please fill in the above question'),
  EE_SPOUSE_7: yup.string().required('Please fill in the above question'),
  CELP_SPOUSE_1: yup.string().required('Please fill in the above question'),
  CELP_SPOUSE_2: yup.string().required('Please fill in the above question'),
  CELP_SPOUSE_3: yup.string().required('Please fill in the above question'),
  CELP_SPOUSE_4: yup.string().required('Please fill in the above question'),
  PEAR_SPOUSE_1: yup.string().required('Please fill in the above question'),
  PEAR_SPOUSE_2: yup.string().required('Please fill in the above question'),
  PEAR_SPOUSE_3: yup.string().required('Please fill in the above question'),
  PEAR_SPOUSE_4: yup.string().required('Please fill in the above question'),
  TEF_SPOUSE_1: yup.string().required('Please fill in the above question'),
  TEF_SPOUSE_2: yup.string().required('Please fill in the above question'),
  TEF_SPOUSE_3: yup.string().required('Please fill in the above question'),
  TEF_SPOUSE_4: yup.string().required('Please fill in the above question'),
  TCF_SPOUSE_1: yup.string().required('Please fill in the above question'),
  TCF_SPOUSE_2: yup.string().required('Please fill in the above question'),
  TCF_SPOUSE_3: yup.string().required('Please fill in the above question'),
  TCF_SPOUSE_4: yup.string().required('Please fill in the above question'),
};

export const LeadEmbedForm: FC<{
  leadEmbedId: string;
  leadEmbed?: any | null;
  form?: Form | null;
}> = ({ leadEmbedId, leadEmbed, form }) => {
  const router = useRouter();

  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isAllowed, setIsAllowed] = useState<boolean>(
    leadEmbed ? leadEmbed?.isActive : false
  );
  const [formName, setFormName] = useState<string | null>(
    leadEmbed ? leadEmbed?.formName : null
  );

  const refresh = async (status: { isMounted: boolean }) => {
    if (leadEmbed && form) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const leadEmbedDb = await Lead.getEmbed({ embedId: leadEmbedId });

    if (status.isMounted) {
      setIsAllowed(leadEmbedDb?.isActive);
      setFormName(leadEmbedDb?.formName);
    } else {
      setIsAllowed(false);
      setFormName(null);
    }

    setIsLoading(false);
  };

  useAsyncEffect(refresh, [leadEmbedId, form, leadEmbed]);

  if (isLoading) {
    return (
      <div className="fixed top-0 bottom-0 left-0 right-0 bg-white z-50 flex items-center">
        <Loading.Form className="bg-white" />
      </div>
    );
  }

  if (!leadEmbedId || !isAllowed || !formName) {
    return <FormFailedMessage />;
  }

  const sendHeightToParent = (height: number) => {
    if (!router.query?.originUrl) {
      return null;
    }

    const postToUrl = decodeURIComponent(router.query.originUrl as string);

    const dataToSend = { message: height };

    window.parent.postMessage(dataToSend, postToUrl);
  };

  const observeElement = (element: any) => {
    if (!element) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newHeight = entry.target.clientHeight;
        sendHeightToParent(newHeight);
      }
    });

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  };

  return (
    <>
      <div ref={(element) => observeElement(element)}>
        {!isSubmitted ? (
          <DynamicApiLeadsForm
            formCache={form}
            formName={formName}
            variation={QUESTION_INPUT_VARIATIONS.GETTING_STARTED}
            leadEmbedId={leadEmbedId}
            afterSubmit={() => {
              setIsSubmitted(true);
            }}
            validationSchemaOverride={
              formName === 'LEADS_CRS_INTAKE'
                ? {
                    schema: LEADS_CRS_FORM_SCHEMA_OVERRIDE,
                  }
                : undefined
            }
          />
        ) : (
          <div className="flex flex-col justify-center items-center">
            <div style={{ height: '80px', width: '80px' }}>
              <AnimateGreenCheck />
            </div>
            <Text.Heading>Form Submitted</Text.Heading>
            <Text.Paragraph>Thank you for submitting the form!</Text.Paragraph>
          </div>
        )}
      </div>
    </>
  );
};
